<template>
    <v-card
        elevation="0"
        :outlined="$vuetify.breakpoint.smAndUp ? true : false"
        max-width="730"
        class="pa-8 mb-10 student-dashboard"
    >
        <v-card-text class="pb-0">
            <!-- Profile Header with Avatar, Name, Roommate Toggle, Progress Bars -->
            <ProfileHeader
                :user="user"
                :university-name="universityName"
                :profile-completion="activeUser.profile_completion"
                @section-click="scrollToSection"
            />

            <v-divider class="my-6"></v-divider>

            <!-- Registration Section -->
            <div ref="registrationSection">
                <RegistrationSection
                    :user="user"
                    @update:user="updateUser"
                />
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Rental Preferences Section -->
            <div ref="rentalSection">
                <RentalPreferencesSection
                    :user="user"
                    @update:user="updateUser"
                />
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Roommate Profile Section -->
            <div ref="roommateSection">
                <RoommateProfileSection
                    :user="user"
                    @update:user="updateUser"
                    @photo-uploaded="onPhotoUploaded"
                />
            </div>

            <!-- Save Button -->
            <v-row class="justify-center mt-8">
                <v-col cols="12" sm="6" md="4" class="pb-0">
                    <v-btn
                        block
                        depressed
                        class="next-btn btn-edurent"
                        color="primary"
                        @click="submit"
                        :loading="isCallingApi"
                        height="48"
                    >Save Changes</v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="4" class="pb-0" v-if="user.needs_roommate">
                    <v-btn
                        outlined
                        block
                        class="btn-edurent preview-btn"
                        height="48"
                        @click="showPreviewDialog = true"
                    >
                        <v-icon left size="18">mdi-eye</v-icon>
                        Preview Profile
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-text>

        <!-- Profile Preview Dialog -->
        <v-dialog v-model="showPreviewDialog" max-width="650" content-class="preview-dialog-wrapper">
            <v-card class="preview-modal-card">
                <v-card-title class="d-flex justify-space-between align-center py-4 px-5">
                    <div>
                        <div class="preview-title">Profile Preview</div>
                        <div class="preview-subtitle">This is how others see your profile</div>
                    </div>
                    <v-btn icon @click="showPreviewDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-8 d-flex justify-center preview-content">
                    <RoommateCard :roommate="previewUser" preview />
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Unsaved Changes Snackbar -->
        <v-snackbar
            v-model="showUnsavedWarning"
            :timeout="-1"
            color="warning"
            top
        >
            You have unsaved changes. Save before leaving?
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="discardChanges">Discard</v-btn>
                <v-btn text v-bind="attrs" @click="saveAndLeave">Save</v-btn>
            </template>
        </v-snackbar>
    </v-card>
</template>

<script>
import { cloneDeep, pick, isEqual } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import ProfileHeader from '@/components/profile/ProfileHeader.vue'
import RegistrationSection from '@/components/profile/RegistrationSection.vue'
import RentalPreferencesSection from '@/components/profile/RentalPreferencesSection.vue'
import RoommateProfileSection from '@/components/profile/RoommateProfileSection.vue'
import RoommateCard from '@/components/RoommateCard.vue'

export default {
    name: 'StudentDashboard',
    components: {
        ProfileHeader,
        RegistrationSection,
        RentalPreferencesSection,
        RoommateProfileSection,
        RoommateCard
    },
    props: {
        activeUser: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            user: cloneDeep(this.activeUser),
            originalUser: cloneDeep(this.activeUser),
            isCallingApi: false,
            showUnsavedWarning: false,
            pendingRoute: null,
            showPreviewDialog: false
        }
    },
    computed: {
        ...mapGetters('Campus', {
            campuses: 'campuses'
        }),
        universityName() {
            const campus = this.campuses.find(c => c.id === this.user.campus_id)
            if (!campus) {
                return ''
            }
            return campus.name
        },
        hasUnsavedChanges() {
            return !isEqual(this.user, this.originalUser)
        },
        previewUser() {
            return {
                id: this.user.id,
                name: this.user.name,
                image_url: this.user.image_url,
                date_of_birth: this.user.date_of_birth,
                university: this.universityName,
                university_start_date: this.user.university_start_date,
                hobbies: this.user.hobbies,
                instagram_username: this.user.instagram_username,
                tiktok_username: this.user.tiktok_username,
                snapchat_username: this.user.snapchat_username,
                facebook_username: this.user.facebook_username,
                phone_number: this.user.phone_number,
                gender: this.user.gender,
                school_id: this.user.school_id,
                school_logo_url: this.user.school_logo_url,
                favorited_by_users: []
            }
        }
    },
    watch: {
        activeUser: {
            handler(newVal) {
                if (!this.user || !this.user.id) {
                    this.user = cloneDeep(newVal)
                    this.originalUser = cloneDeep(newVal)
                }
            },
            deep: true
        }
    },
    methods: {
        ...mapActions('Campus', {
            getCampuses: 'getCampuses'
        }),
        updateUser(updatedUser) {
            this.user = { ...this.user, ...updatedUser }
        },
        onPhotoUploaded(imageUrl) {
            this.user.image_url = imageUrl
            this.$store.dispatch('getUserByToken')
        },
        scrollToSection(sectionKey) {
            const sectionRefs = {
                registration: 'registrationSection',
                rental_profile: 'rentalSection',
                roommate_profile: 'roommateSection'
            }

            if (sectionKey === 'campaign') {
                this.$router.push('/crowdfunding')
                return
            }

            const refName = sectionRefs[sectionKey]
            if (refName && this.$refs[refName]) {
                const element = this.$refs[refName]
                const headerOffset = 75 // Account for fixed header + some padding
                const elementPosition = element.getBoundingClientRect().top
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                })
            }
        },
        async submit() {
            const updatedUser = pick(this.user, [
                // User ID
                'id',
                // Registration
                'gender',
                'date_of_birth',
                'program',
                // Rental Preferences
                'campus_id',
                'school_id',
                'university_start_date',
                'preferred_bedrooms',
                'preferred_bathrooms',
                'preferred_max_price',
                'preferred_pet_friendly',
                'user_amenities',
                // Roommate Profile
                'needs_roommate',
                'hobbies',
                'ideal_roommate_gender',
                'ideal_roommate_age_young',
                'ideal_roommate_age_old',
                'allow_roommate_sms',
                'facebook_username',
                'snapchat_username',
                'tiktok_username',
                'instagram_username'
            ])

            // Set school_id from campus if available
            if (updatedUser.campus_id && this.campuses.length > 0) {
                const campus = this.campuses.find(c => c.id === updatedUser.campus_id)
                if (campus) {
                    updatedUser.school_id = campus.school_id
                }
            }

            this.isCallingApi = true
            await this.$store.dispatch('updateUser', updatedUser)
            this.isCallingApi = false

            // Refresh user data
            await this.$store.dispatch('getUserByToken')

            // Update original user to reflect saved state
            this.originalUser = cloneDeep(this.user)
        },
        discardChanges() {
            this.user = cloneDeep(this.originalUser)
            this.showUnsavedWarning = false
            if (this.pendingRoute) {
                this.$router.push(this.pendingRoute)
            }
        },
        async saveAndLeave() {
            await this.submit()
            this.showUnsavedWarning = false
            if (this.pendingRoute) {
                this.$router.push(this.pendingRoute)
            }
        },
        handleBeforeUnload(e) {
            if (this.hasUnsavedChanges) {
                e.preventDefault()
                e.returnValue = ''
            }
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.hasUnsavedChanges) {
            this.pendingRoute = to.fullPath
            this.showUnsavedWarning = true
            next(false)
        } else {
            next()
        }
    },
    async beforeMount() {
        if (Object.keys(this.user).length === 0) {
            return
        }

        if (this.campuses.length === 0) {
            await this.getCampuses()
        }
    },
    mounted() {
        window.addEventListener('beforeunload', this.handleBeforeUnload)
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload)
    }
}
</script>

<style>
.student-dashboard h3 {
    font-size: 26px !important;
    font-weight: 700;
    color: #000;
}

.student-dashboard h4 {
    font-size: 20px !important;
    font-weight: 700;
    color: #000;
}

.student-dashboard h5 {
    font-size: 14px !important;
    font-weight: 700;
    color: #000;
}

.student-dashboard .field-selected {
    background: rgba(45, 118, 227, 0.1);
}

.student-dashboard .v-btn__content {
    color: #000000 !important;
}

.student-dashboard .next-btn .v-btn__content {
    color: white !important;
}

.student-dashboard .v-slider--horizontal .v-slider__track-container {
    height: 4px;
}

.student-dashboard .preview-btn .v-btn__content {
    color: #666 !important;
}

.preview-modal-card {
    overflow: hidden !important;
    border-radius: 16px !important;
}

.preview-modal-card .v-card__text {
    overflow: visible !important;
}

.preview-title {
    font-size: 20px;
    font-weight: 600;
    color: #000;
}

.preview-subtitle {
    font-size: 13px;
    color: #666;
    margin-top: 2px;
}

.preview-content {
    background: #fafafa;
    min-height: auto;
    padding: 32px !important;
}
</style>
